/**
 * Wizard del estimador — pasos VISUALES tipo "no brainer".
 * Modelo mental claro: 1) ¿cuál baño?  2) ¿qué alcance?  3) detalles según el alcance.
 * Los pasos de detalle (ducha/tina/vanity) solo aparecen si aplican al scope elegido.
 * Solo se pregunta lo que MUEVE el rango (los acabados como unidad = Fase 2).
 */
import { PRICING } from "./pricing.config";

export type PriceRange = readonly [number, number];
export type Sel = Record<string, string>;

export interface EstimatorOption {
  id: string;
  label: string;
  sub?: string;
  badge?: string;
  img: string;
  price: PriceRange;
}

export interface EstimatorStep {
  key: "room" | "scope" | "style" | "shower" | "tub" | "vanity";
  title: string;
  help?: string;
  cols: 2 | 3;
  /** Si se define, el paso solo se muestra cuando devuelve true (según selecciones previas). */
  showIf?: (sel: Sel) => boolean;
  options: EstimatorOption[];
}

const KEEP: PriceRange = [0, 0];
const S = "/img/styles";
const O = "/img/opts";
const R = "/img/rooms";

/** ¿El scope incluye trabajo de ducha? (full / shower only / tub-to-shower) */
const scopeHasShower = (s: Sel) => !s.scope || ["full", "shower", "tub2shower"].includes(s.scope);
/** ¿El scope incluye tina + vanity? (full / everything-but-shower) */
const scopeHasRoom = (s: Sel) => !s.scope || ["full", "refresh"].includes(s.scope);

export const ESTIMATOR_STEPS: EstimatorStep[] = [
  {
    key: "room",
    title: "Which bathroom is it?",
    help: "Just pick the room — we confirm sizes on the visit.",
    cols: 3,
    options: [
      { id: "master", label: "Master bath", sub: "Primary suite", badge: "Most popular", img: `${R}/master.webp`, price: PRICING.room.master },
      { id: "secondary", label: "Secondary bath", sub: "Guest / hall · full", img: `${R}/secondary.webp`, price: PRICING.room.secondary },
      { id: "half", label: "Half bath", sub: "Powder · no shower", img: `${R}/half.webp`, price: PRICING.room.half },
    ],
  },
  {
    key: "scope",
    title: "What are we doing?",
    help: "Pick the scope of work.",
    cols: 2,
    options: [
      { id: "full", label: "Full renovation", sub: "Everything, down to the studs", badge: "Most popular", img: `${S}/spa.webp`, price: PRICING.scope.full },
      { id: "shower", label: "Shower only", sub: "Rebuild just the shower", img: `${O}/glass-frameless.webp`, price: PRICING.scope.shower },
      { id: "tub2shower", label: "Tub-to-shower", sub: "Turn the tub into a walk-in", img: `${O}/entry-curbless.webp`, price: PRICING.scope.tub_to_shower },
      { id: "refresh", label: "Everything but the shower", sub: "Keep the shower, redo the rest", img: `${O}/vanityCfg-double.webp`, price: PRICING.scope.refresh },
    ],
  },
  {
    key: "style",
    title: "Which look do you love?",
    help: "Just tap the vibe you're drawn to.",
    cols: 3,
    options: [
      { id: "spa", label: "Modern Spa", img: `${S}/spa.webp`, price: KEEP },
      { id: "coastal", label: "Coastal", img: `${S}/coastal.webp`, price: KEEP },
      { id: "organic", label: "Organic", img: `${S}/organic.webp`, price: KEEP },
      { id: "farmhouse", label: "Farmhouse", img: `${S}/farmhouse.webp`, price: KEEP },
      { id: "glam", label: "Glam", img: `${S}/glam.webp`, price: KEEP },
      { id: "transitional", label: "Timeless", img: `${S}/transitional.webp`, price: KEEP },
    ],
  },
  {
    key: "shower",
    title: "Your shower?",
    help: "Tap one.",
    cols: 3,
    showIf: scopeHasShower,
    options: [
      { id: "walkin", label: "Glass walk-in", sub: "Frameless glass", badge: "Most popular", img: `${O}/glass-frameless.webp`, price: PRICING.showerType.walkin },
      { id: "wetroom", label: "Curbless wet room", sub: "Spa-like, no step", img: `${O}/entry-curbless.webp`, price: PRICING.showerType.wetroom },
      { id: "spa", label: "Rain + body spray", sub: "Full spa system", img: `${O}/showerSys-rain.webp`, price: PRICING.showerType.spa },
    ],
  },
  {
    key: "tub",
    title: "Want a tub?",
    help: "Tap one.",
    cols: 3,
    showIf: scopeHasRoom,
    options: [
      { id: "none", label: "No tub", sub: "Shower only", img: `${O}/tub-none.webp`, price: PRICING.tub.none },
      { id: "freestanding", label: "Freestanding", sub: "Standalone soaker", img: `${O}/tub-freestanding.webp`, price: PRICING.tub.freestanding },
      { id: "combo", label: "Shower-tub", sub: "Tub under shower", img: `${O}/tub-alcove.webp`, price: PRICING.tub.combo },
    ],
  },
  {
    key: "vanity",
    title: "One sink or two?",
    cols: 2,
    showIf: scopeHasRoom,
    options: [
      { id: "single", label: "One sink", sub: "Single vanity", img: `${O}/vanityCfg-single.webp`, price: PRICING.vanity.single },
      { id: "double", label: "Two sinks", sub: "Double vanity", img: `${O}/vanityCfg-double.webp`, price: PRICING.vanity.double },
    ],
  },
];

export const TIMELINES = [
  { id: "asap", label: "As soon as possible", emoji: "🔥" },
  { id: "1-3m", label: "In 1–3 months", emoji: "📆" },
  { id: "exploring", label: "Just exploring", emoji: "✨" },
] as const;
